import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Dialog,
    DialogContent,
    Button,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Checkbox,
    Tooltip,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import AssetService from '../../../../Services/assets.service';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import {
    getAssetId,
    mapAssetToReplacementRow,
    mapSavedReplacementToRow,
    getBeforeReplacementCost,
    computeAfterReplacementCost,
    computeReplacementTotals,
    clampReplacementRowField,
    buildReplacementSaveRows,
    getAvailableRealGainForReplacement,
    getAvailableInflationaryGainForReplacement,
} from '../../../../Utils/capitalGainCalculations';

const REPLACEMENT_VALIDATION_MESSAGES = {
    replacementNegative: 'סכום השחלוף לא יכול להיות שלילי',
    exceedsBeforeBasis: 'סכום השחלוף לא יכול להיות גבוה מהיתרה לפני שחלוף',
    realExceedsGain: 'סכום שחלוף ריאלי גבוה מהרווח הריאלי הזמין',
    inflationaryExceedsGain: "סכום שחלוף אינפ' חייב גבוה מהסכום האינפלציוני החייב",
};

const HISULIT_PURPLE = '#7b1fa2';

const formatDateHe = (d) =>
    !d
        ? '—'
        : new Date(d).toLocaleDateString('he-IL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
          });

const normalizeText = (value) =>
    String(value ?? '')
        .toLowerCase()
        .trim();

const isRealReplacementAmountInvalid = (value) => {
    if (value === '' || value === null || value === undefined) return true;
    return Number(value) <= 0;
};

const getReplacementRowKey = (row) =>
    row?.id ||
    row?.replacementId ||
    row?.assetId ||
    row?.replacementAssetId ||
    row?.ReplacementAssetId;

const headerCellSx = {
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1.25,
    textAlign: 'center',
    py: 0.75,
    px: 0.5,
    whiteSpace: 'nowrap',
    bgcolor: '#eceff1',
    borderBottom: '2px solid #304FFF',
};

const bodyCellSx = {
    textAlign: 'center',
    fontSize: 12,
    lineHeight: 1.25,
    py: 0.5,
    px: 0.5,
    direction: 'ltr',
};

const inputFieldSx = {
    width: 90,
    mx: 'auto',
    '& .MuiInputBase-root': { height: 26, backgroundColor: '#fff' },
    '& .MuiInputBase-input': {
        py: 0,
        px: 0.5,
        fontSize: 12,
        textAlign: 'center',
        direction: 'ltr',
    },
};

const CapitalGainReplacementModal = ({
    open = false,
    onClose = () => {},
    onApply = () => {},
    direction = 'rtl',
    caseId,
    token,
    excludedAssetId = null,
    soldAssetId = null,
    initialReplacementRows = [],
    canApplyReplacement = true,
    maxRealGain = 0,
    maxInflationaryGain = 0,
    gains = null,
    saleDate = null,
    parentDepreciationPercentage = 0,
}) => {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';

    const [candidateAssets, setCandidateAssets] = useState([]);
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [replacementRows, setReplacementRows] = useState([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    const [replacementSearchTerm, setReplacementSearchTerm] = useState('');
    const [realReplacementFieldErrors, setRealReplacementFieldErrors] = useState(new Set());

    const reductionContext = useMemo(
        () => ({ saleDate, parentDepreciationPercentage }),
        [saleDate, parentDepreciationPercentage]
    );

    const fetchReplacementCandidates = useCallback(async () => {
        if (!caseId || !token || !soldAssetId) {
            setCandidateAssets([]);
            return;
        }

        setLoadingCandidates(true);
        try {
            const response = await AssetService.GetCapitalGainReplacementCandidates(
                caseId,
                soldAssetId,
                token
            );
            if (!response?.success) {
                throw new Error(response?.error || t('FailedToFetchAssets'));
            }
            setCandidateAssets(response.data || []);
            setSelectedCandidateIds([]);
        } catch (error) {
            toast.error(error?.message || t('FailedToFetchAssets'));
            setCandidateAssets([]);
        } finally {
            setLoadingCandidates(false);
        }
    }, [caseId, token, soldAssetId, t]);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;
        setSelectedCandidateIds([]);
        setReplacementSearchTerm('');
        setRealReplacementFieldErrors(new Set());

        const loadSaved = async () => {
            setLoadingSaved(true);
            try {
                if (soldAssetId && caseId && token) {
                    const response = await AssetService.GetCapitalGainReplacements(
                        caseId,
                        soldAssetId,
                        token
                    );
                    if (cancelled) return;
                    if (response?.success && Array.isArray(response.data) && response.data.length > 0) {
                        setReplacementRows(
                            response.data.map((row) => mapSavedReplacementToRow(row, reductionContext))
                        );
                        return;
                    }
                }

                setReplacementRows(
                    (initialReplacementRows || []).map((row) =>
                        row.assetId != null ? row : mapAssetToReplacementRow(row, reductionContext)
                    )
                );
            } catch (error) {
                if (!cancelled) {
                    toast.error(error?.message || t('FailedToLoadCapitalGainData'));
                    setReplacementRows(
                        (initialReplacementRows || []).map((row) =>
                            row.assetId != null ? row : mapAssetToReplacementRow(row, reductionContext)
                        )
                    );
                }
            } finally {
                if (!cancelled) setLoadingSaved(false);
            }
        };

        loadSaved();

        return () => {
            cancelled = true;
        };
    }, [open, soldAssetId, caseId, token, initialReplacementRows, reductionContext, t]);

    useEffect(() => {
        if (!open || !caseId || !soldAssetId) return;
        fetchReplacementCandidates();
    }, [open, caseId, soldAssetId, fetchReplacementCandidates]);

    const selectedReplacementIds = useMemo(
        () => new Set(replacementRows.map((row) => row.assetId)),
        [replacementRows]
    );

    const filteredCandidateAssets = useMemo(() => {
        const search = normalizeText(replacementSearchTerm);
        if (!search) return candidateAssets;

        return candidateAssets.filter((asset) => {
            const assetNumber = normalizeText(asset.assetNumber ?? asset.AssetNumber);
            const name = normalizeText(asset.assetName ?? asset.AssetName ?? asset.name ?? asset.Name);
            const description = normalizeText(asset.description ?? asset.Description);
            const assetGroupId = normalizeText(asset.assetGroupId ?? asset.AssetGroupId);

            return (
                assetNumber.includes(search) ||
                name.includes(search) ||
                description.includes(search) ||
                assetGroupId.includes(search)
            );
        });
    }, [candidateAssets, replacementSearchTerm]);

    const availableCandidates = useMemo(
        () =>
            filteredCandidateAssets.filter((asset) => !selectedReplacementIds.has(getAssetId(asset))),
        [filteredCandidateAssets, selectedReplacementIds]
    );

    const totals = useMemo(() => computeReplacementTotals(replacementRows), [replacementRows]);

    const availableRealGainForReplacement = useMemo(
        () => getAvailableRealGainForReplacement(gains),
        [gains]
    );

    const availableInflationaryGainForReplacement = useMemo(
        () => getAvailableInflationaryGainForReplacement(gains),
        [gains]
    );

    useEffect(() => {
        if (!open) return;

        setReplacementRows((prev) => {
            let changed = false;
            const next = prev.map((row) => {
                let updated = row;
                if (
                    availableRealGainForReplacement <= 0 &&
                    Number(row.realReplacementAmount || 0) !== 0
                ) {
                    updated = { ...updated, realReplacementAmount: 0 };
                    changed = true;
                }
                if (
                    availableInflationaryGainForReplacement <= 0 &&
                    Number(row.inflationaryReplacementAmount || 0) !== 0
                ) {
                    updated = { ...updated, inflationaryReplacementAmount: 0 };
                    changed = true;
                }
                return updated;
            });
            return changed ? next : prev;
        });
    }, [open, availableRealGainForReplacement, availableInflationaryGainForReplacement]);

    const handleToggleCandidate = (assetId) => {
        setSelectedCandidateIds((prev) =>
            prev.includes(assetId)
                ? prev.filter((id) => id !== assetId)
                : [...prev, assetId]
        );
    };

    const handleMoveSelectedToReplacement = () => {
        const selectedAssets = availableCandidates.filter((asset) =>
            selectedCandidateIds.includes(getAssetId(asset))
        );

        setReplacementRows((prev) => {
            const existingIds = new Set(prev.map((row) => row.assetId));
            const newRows = selectedAssets
                .filter((asset) => !existingIds.has(getAssetId(asset)))
                .map((asset) => mapAssetToReplacementRow(asset, reductionContext));
            return [...prev, ...newRows];
        });

        setSelectedCandidateIds([]);
    };

    const handleRemoveReplacementRow = async (row) => {
        const assetName = row.assetName || row.name || row.Name || '';
        const assetNumber = row.assetNumber || row.AssetNumber || '';

        const result = await Swal.fire({
            title: 'האם אתה בטוח?',
            text: `האם להסיר את נכס השחלוף ${assetNumber ? `${assetNumber} - ` : ''}${assetName}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'כן, הסר',
            cancelButtonText: 'ביטול',
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        try {
            const replacementId = row.id || row.replacementId;
            if (replacementId && caseId && token) {
                const response = await AssetService.DeleteCapitalGainReplacement(
                    caseId,
                    replacementId,
                    token
                );
                if (!response?.success) {
                    throw new Error(response?.error || t('FailedToSaveCapitalGainReplacements'));
                }
            }

            const nextRows = replacementRows.filter(
                (item) => getReplacementRowKey(item) !== getReplacementRowKey(row)
            );
            setReplacementRows(nextRows);

            const nextTotals = computeReplacementTotals(nextRows);
            onApply({
                selectedAssets: nextRows,
                realReplacementAmount: nextTotals.totalRealReplacementAmount,
                inflationaryReplacementAmount: nextTotals.totalInflationaryReplacementAmount,
            });

            toast.success('נכס השחלוף הוסר בהצלחה');
        } catch (error) {
            console.error('Failed to remove replacement asset:', error);
            toast.error('שגיאה בהסרת נכס השחלוף');
        }
    };

    const handleRowAmountChange = (assetId, field, rawValue) => {
        if (field === 'realReplacementAmount') {
            setRealReplacementFieldErrors((prev) => {
                if (!prev.has(assetId)) return prev;
                if (isRealReplacementAmountInvalid(rawValue)) return prev;
                const next = new Set(prev);
                next.delete(assetId);
                return next;
            });
        }

        setReplacementRows((prev) =>
            prev.map((row) => {
                if (row.assetId !== assetId) return row;
                const clamped = clampReplacementRowField({
                    row,
                    field,
                    rawValue,
                    replacementRows: prev,
                    maxRealGain,
                    maxInflationaryGain,
                });
                if (clamped.errorCode) {
                    const message =
                        t(clamped.errorCode) ||
                        REPLACEMENT_VALIDATION_MESSAGES[clamped.errorCode];
                    if (message) toast.error(message);
                }
                const { errorCode, ...values } = clamped;
                return { ...row, ...values };
            })
        );
    };

    const handleApply = async () => {
        if (!caseId || !token || !soldAssetId) {
            toast.error(t('FailedToSaveCapitalGainReplacements') || 'שמירת שחלוף נכסים נכשלה');
            return;
        }

        const invalidRealReplacementIds =
            availableRealGainForReplacement > 0
                ? replacementRows
                      .filter((row) => isRealReplacementAmountInvalid(row.realReplacementAmount))
                      .map((row) => row.assetId)
                      .filter(Boolean)
                : [];

        if (invalidRealReplacementIds.length > 0) {
            setRealReplacementFieldErrors(new Set(invalidRealReplacementIds));
            toast.error(
                t('RealReplacementAmountRequired') ||
                    'יש להזין סכום שחלוף ריאלי גדול מאפס לכל נכס שנבחר'
            );
            return;
        }

        setRealReplacementFieldErrors(new Set());

        if (totals.totalRealReplacementAmount > availableRealGainForReplacement) {
            toast.error('סכום שחלוף ריאלי גבוה מהרווח הריאלי החייב');
            return;
        }

        if (totals.totalInflationaryReplacementAmount > availableInflationaryGainForReplacement) {
            toast.error('סכום שחלוף אינפ׳ חייב גבוה מהסכום האינפלציוני החייב');
            return;
        }

        const taxYear = saleDate ? new Date(saleDate).getFullYear() : new Date().getFullYear();
        const payload = {
            soldAssetId,
            taxYear,
            rows: buildReplacementSaveRows(replacementRows, gains),
        };

        setSaving(true);
        try {
            const response = await AssetService.SaveCapitalGainReplacements(caseId, payload, token);
            if (!response?.success) {
                throw new Error(response?.error || t('FailedToSaveCapitalGainReplacements'));
            }

            const savedRows = (response.data || []).map((row) =>
                mapSavedReplacementToRow(row, reductionContext)
            );
            const savedTotals = computeReplacementTotals(savedRows);

            setReplacementRows(savedRows);
            onApply({
                selectedAssets: savedRows,
                realReplacementAmount: savedTotals.totalRealReplacementAmount,
                inflationaryReplacementAmount: savedTotals.totalInflationaryReplacementAmount,
            });
            toast.success('נתוני השחלוף נשמרו בהצלחה');
        } catch (error) {
            console.error('Failed to apply asset replacement:', error);
            toast.error(error?.message || t('FailedToSaveCapitalGainReplacements') || 'שגיאה בשמירת נתוני השחלוף');
        } finally {
            setSaving(false);
        }
    };

    const renderEmptyRow = (message, colSpan) => (
        <TableRow>
            <TableCell colSpan={colSpan} sx={{ ...bodyCellSx, color: '#999', py: 2, direction }}>
                {message}
            </TableCell>
        </TableRow>
    );

    const tableSx = {
        direction,
        minWidth: 900,
        '& .MuiTableCell-root': {
            borderColor: '#e8eaf6',
        },
    };

    const candidateTableSx = {
        ...tableSx,
        minWidth: 700,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            dir={direction}
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    border: '1px solid #304FFF',
                    boxShadow: '0 8px 32px rgba(48, 79, 255, 0.18)',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.25,
                    background: 'linear-gradient(135deg, #304FFF 0%, #5c6bc0 100%)',
                    color: '#fff',
                    direction,
                }}
            >
                <IconButton size="small" onClick={onClose} sx={{ color: '#fff', p: 0.5 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1, textAlign: isRtl ? 'left' : 'right' }}>
                    {t('ReplacementAssetsPickerTitle') || 'שחלוף נכסים'}
                </Typography>
            </Box>

            <DialogContent
                sx={{
                    direction,
                    p: 2,
                    bgcolor: '#fafbff',
                    '&.MuiDialogContent-root': {
                        overflowY: 'visible',
                        overflow: 'visible',
                    },
                }}
            >
                {!canApplyReplacement ? (
                    <Typography sx={{ fontSize: 13, color: '#666', textAlign: isRtl ? 'left' : 'right' }}>
                        {t('NoTaxableGainForReplacement') || 'אין רווח חייב לשחלוף'}
                    </Typography>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                mb: 2,
                                justifyContent: isRtl ? 'flex-start' : 'flex-end',
                                direction,
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#304FFF' }}>
                                {t('TotalReplacement') || 'סה"כ שחלוף'}:{' '}
                                {beautifyNumber(totals.totalReplacement, 0)}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                {t('TotalInflationaryReplacement') || 'סה"כ שחלוף אינפ\' חייב'}:{' '}
                                {beautifyNumber(totals.totalInflationaryReplacementAmount, 0)}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                {t('TotalRealReplacement') || 'סה"כ שחלוף ריאלי'}:{' '}
                                {beautifyNumber(totals.totalRealReplacementAmount, 0)}
                            </Typography>
                        </Box>

                        {(availableRealGainForReplacement <= 0 ||
                            availableInflationaryGainForReplacement <= 0) && (
                            <Box sx={{ mb: 1.5, direction }}>
                                {availableRealGainForReplacement <= 0 && (
                                    <Typography sx={{ fontSize: 12, color: '#d32f2f', textAlign: isRtl ? 'left' : 'right' }}>
                                        אין רווח ריאלי חייב לשחלוף
                                    </Typography>
                                )}
                                {availableInflationaryGainForReplacement <= 0 && (
                                    <Typography sx={{ fontSize: 12, color: '#d32f2f', textAlign: isRtl ? 'left' : 'right' }}>
                                        אין סכום אינפ׳ חייב לשחלוף
                                    </Typography>
                                )}
                            </Box>
                        )}

                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                mb: 0.75,
                                textAlign: isRtl ? 'left' : 'right',
                            }}
                        >
                            {t('SelectedReplacementAssets') || 'נכסים שנבחרו לשחלוף'}
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ direction }}>
                            <Table size="small" dir="rtl">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headerCellSx}>{t('Year') || 'שנה'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('AssetNumber') || 'מס׳ נכס'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('AssetName') || 'שם הנכס'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('PurchaseDate') || 'תאריך רכישה'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('OriginalPrice') || 'מחיר מקורי'}</TableCell>
                                        <TableCell sx={headerCellSx}>
                                            {t('BeforeCurrentReplacement') || 'לפני שחלוף נוכחי'}
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            {t('RealReplacementAmount') || 'סכום שחלוף ריאלי'}
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            {t('InflationaryReplacementAmount') || "סכום שחלוף אינפ' חייב"}
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>
                                            {t('AfterCurrentReplacement') || 'אחרי שחלוף נוכחי'}
                                        </TableCell>
                                        <TableCell sx={{ ...headerCellSx, width: 48 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {replacementRows.length === 0
                                        ? renderEmptyRow(
                                              t('NoSelectedReplacementAssets') ||
                                                  'לא נבחרו נכסים לשחלוף',
                                              10
                                          )
                                        : replacementRows.map((row) => (
                                              <TableRow key={row.assetId} hover>
                                                  <TableCell sx={bodyCellSx}>{row.year ?? '—'}</TableCell>
                                                  <TableCell sx={bodyCellSx}>{row.assetNumber ?? '—'}</TableCell>
                                                  <TableCell sx={{ ...bodyCellSx, maxWidth: 140 }}>
                                                      {row.assetName ?? '—'}
                                                  </TableCell>
                                                  <TableCell sx={bodyCellSx}>
                                                      {formatDateHe(row.purchaseDate)}
                                                  </TableCell>
                                                  <TableCell sx={bodyCellSx}>
                                                      {beautifyNumber(row.originalPrice, 0)}
                                                  </TableCell>
                                                  <TableCell sx={bodyCellSx}>
                                                      {beautifyNumber(getBeforeReplacementCost(row), 0)}
                                                  </TableCell>
                                                  <TableCell sx={bodyCellSx}>
                                                      <TextField
                                                          size="small"
                                                          type="number"
                                                          value={row.realReplacementAmount ?? 0}
                                                          error={realReplacementFieldErrors.has(row.assetId)}
                                                          disabled={availableRealGainForReplacement <= 0}
                                                          onChange={(e) =>
                                                              handleRowAmountChange(
                                                                  row.assetId,
                                                                  'realReplacementAmount',
                                                                  e.target.value
                                                              )
                                                          }
                                                          inputProps={{ min: 0 }}
                                                          sx={inputFieldSx}
                                                      />
                                                  </TableCell>
                                                  <TableCell sx={bodyCellSx}>
                                                      <TextField
                                                          size="small"
                                                          type="number"
                                                          value={row.inflationaryReplacementAmount ?? 0}
                                                          disabled={availableInflationaryGainForReplacement <= 0}
                                                          onChange={(e) =>
                                                              handleRowAmountChange(
                                                                  row.assetId,
                                                                  'inflationaryReplacementAmount',
                                                                  e.target.value
                                                              )
                                                          }
                                                          inputProps={{ min: 0 }}
                                                          sx={inputFieldSx}
                                                      />
                                                  </TableCell>
                                                  <TableCell sx={bodyCellSx}>
                                                      {beautifyNumber(computeAfterReplacementCost(row), 0)}
                                                  </TableCell>
                                                  <TableCell sx={bodyCellSx}>
                                                      <IconButton
                                                          size="small"
                                                          color="error"
                                                          onClick={() => handleRemoveReplacementRow(row)}
                                                      >
                                                          <DeleteOutlineIcon fontSize="small" />
                                                      </IconButton>
                                                  </TableCell>
                                              </TableRow>
                                          ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                my: 1,
                            }}
                        >
                            <Tooltip title={t('MoveToReplacement') || 'העלה לשחלוף'}>
                                <span>
                                    <IconButton
                                        onClick={handleMoveSelectedToReplacement}
                                        disabled={selectedCandidateIds.length === 0}
                                        sx={{
                                            bgcolor: '#304FFF',
                                            color: '#fff',
                                            borderRadius: '4px',
                                            '&:hover': { bgcolor: '#253dcc' },
                                            '&.Mui-disabled': { bgcolor: '#b0b8f5', color: '#fff' },
                                        }}
                                    >
                                        <ArrowUpwardIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                mb: 1,
                                direction,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    textAlign: isRtl ? 'left' : 'right',
                                }}
                            >
                                {t('PotentialReplacementAssets') || 'נכסים פוטנציאליים לשחלוף'}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: isRtl ? 'flex-end' : 'flex-start',
                                    direction,
                                }}
                            >
                                <TextField
                                    placeholder={
                                        t('Search')}
                                    value={replacementSearchTerm}
                                    onChange={(e) => setReplacementSearchTerm(e.target.value)}
                                    className="search-bar"
                                    style={{ height: '40px', width: '230px', flexShrink: 0 }}
                                    variant="standard"
                                    InputProps={{
                                        disableUnderline: true,
                                        className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                        sx: {
                                            height: '40px',
                                            padding: '0 10px',
                                            borderRadius: '8px',
                                            background: '#F5F5F6',
                                            border: '1px solid transparent',
                                            '&:focus-within': {
                                                border: '2px solid var(--Foundation-Blue-Normal, #304FFF)',
                                            },
                                        },
                                        ...(direction === 'ltr'
                                            ? {
                                                  endAdornment: (
                                                      <InputAdornment position="end">
                                                          <IconButton
                                                              edge="end"
                                                              style={{ marginRight: '1px', color: '#6c757d' }}
                                                          >
                                                              <SearchIcon />
                                                          </IconButton>
                                                      </InputAdornment>
                                                  ),
                                              }
                                            : {
                                                  startAdornment: (
                                                      <InputAdornment position="start">
                                                          <IconButton
                                                              edge="start"
                                                              style={{ marginLeft: '1px', color: '#6c757d' }}
                                                          >
                                                              <SearchIcon />
                                                          </IconButton>
                                                      </InputAdornment>
                                                  ),
                                              }),
                                    }}
                                    inputProps={{
                                        style: {
                                            textAlign: direction === 'rtl' ? 'right' : 'left',
                                            direction: direction === 'rtl' ? 'rtl' : 'ltr',
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                        <TableContainer component={Paper} variant="outlined" sx={{ direction, position: 'relative' }}>
                            {loadingCandidates && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255,255,255,0.65)',
                                        zIndex: 1,
                                    }}
                                >
                                    <CircularProgress size={28} />
                                </Box>
                            )}
                            <Table size="small" sx={candidateTableSx}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                ...headerCellSx,
                                                bgcolor: '#f0f3ff',
                                                color: HISULIT_PURPLE,
                                            }}
                                        >
                                            {t('ReductionBalance') || 'יתרה להפחתה'}
                                        </TableCell>
                                        <TableCell sx={headerCellSx}>{t('OriginalPrice') || 'עלות'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('PurchaseDate') || 'תאריך רכישה'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('AssetName') || 'שם הנכס'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('AssetNumber') || 'מספר נכס'}</TableCell>
                                        <TableCell sx={headerCellSx}>{t('Year') || 'שנה'}</TableCell>
                                        <TableCell sx={{ ...headerCellSx, width: 48 }} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {availableCandidates.length === 0
                                        ? renderEmptyRow(
                                              t('NoPotentialReplacementAssets') ||
                                                  'לא נמצאו נכסים פוטנציאליים לשחלוף',
                                              7
                                          )
                                        : availableCandidates.map((asset) => {
                                              const assetId = getAssetId(asset);
                                              const row = mapAssetToReplacementRow(asset, reductionContext);
                                              const isSelected = selectedCandidateIds.includes(assetId);
                                              return (
                                                  <TableRow
                                                      key={assetId}
                                                      hover
                                                      selected={isSelected}
                                                      onClick={() => handleToggleCandidate(assetId)}
                                                      sx={{
                                                          cursor: 'pointer',
                                                          ...(isSelected && {
                                                              boxShadow: isRtl
                                                                  ? 'inset -4px 0 0 #304FFF'
                                                                  : 'inset 4px 0 0 #304FFF',
                                                          }),
                                                      }}
                                                  >
                                                      <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }}>
                                                          {beautifyNumber(row.remainingCost, 0)}
                                                      </TableCell>
                                                      <TableCell sx={bodyCellSx}>
                                                          {beautifyNumber(row.originalPrice, 0)}
                                                      </TableCell>
                                                      <TableCell sx={bodyCellSx}>
                                                          {formatDateHe(row.purchaseDate)}
                                                      </TableCell>
                                                      <TableCell sx={{ ...bodyCellSx, maxWidth: 140 }}>
                                                          {row.assetName ?? '—'}
                                                      </TableCell>
                                                      <TableCell sx={bodyCellSx}>{row.assetNumber ?? '—'}</TableCell>
                                                      <TableCell sx={bodyCellSx}>{row.year ?? '—'}</TableCell>
                                                      <TableCell sx={bodyCellSx} padding="checkbox">
                                                          <Checkbox
                                                              size="small"
                                                              checked={isSelected}
                                                              onChange={() => handleToggleCandidate(assetId)}
                                                              onClick={(e) => e.stopPropagation()}
                                                          />
                                                      </TableCell>
                                                  </TableRow>
                                              );
                                          })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: isRtl ? 'flex-start' : 'flex-end',
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    borderTop: '1px solid #e3e8ff',
                    bgcolor: '#fff',
                    direction,
                }}
            >
                <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>
                    {t('Cancel') || 'ביטול'}
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    size="small"
                    disabled={!canApplyReplacement || saving || loadingSaved}
                    sx={{
                        bgcolor: '#304FFF',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#253dcc' },
                    }}
                >
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('Apply') || 'אישור'}
                </Button>
            </Box>
        </Dialog>
    );
};

export default CapitalGainReplacementModal;
